import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { convertToFilter, filterObj, useDebounce } from "../../../util";
import useUser from "../../../hooks/useUser";
import {
  // FilterBar,
  Pagination,
  Tabs,
} from "../../ui";
import TableUI from "../../Table";

const Table = props => {
  const {
    className = "",
    dataKey,
    debounceDelay = 500,
    emptyMessage,
    FETCH_QUERY,
    footerComponent,
    headers = [],
    isAdmin,
    limit = 50,
    pagination = {},
    skip,
    tabProps,
    pollInterval,
    REFETCH_QUERIES = [],
    UPDATE_MUTATION,
    onMutationError = () => {},
    variables = {},
    variant = "modal",
    fetchPolicy = null,
    rowClassName,
    formik: externalFormik,
    allowColumnWrap,
    allowHorizontalScroll = false,
    onQueryStateChange,
    showRefresh = true,
    isSubTable = false,
    filterInputVariables = false,
    sequentialRowUpdates = false,
  } = props || {};
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { hasPermission } = useUser();
  const canShowRefresh = showRefresh && hasPermission("ADMIN_MODULE");

  const internalFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...(isAdmin ? { isAdmin } : {}),
      limit,
      offset: 0,
      ...variables,
    },
  });

  const formik = externalFormik ?? internalFormik;

  const debouncedObj = useDebounce(
    { values: formik?.values, FETCH_QUERY },
    debounceDelay,
  );
  // don't include null or empty values in query

  const values = filterObj(
    debouncedObj?.values,
    value => value !== undefined && value !== null && value !== "",
  );

  // When using external formik, only send variables that are valid for the GraphQL query
  // (limit, offset, and keys from the variables prop). Client-only state (e.g. selected) is omitted.
  const queryVariableKeys = new Set([
    "limit",
    "offset",
    "order",
    "sort1",
    "sort2",
    "sort3",
    ...Object.keys(variables),
  ]);
  const queryVariables = Object.fromEntries(
    Object.entries(values).filter(([key]) => queryVariableKeys.has(key)),
  );
  queryVariables.limit = queryVariables.limit ?? limit;
  queryVariables.offset = queryVariables.offset ?? 0;

  // For V2 resolvers (e.g. contactsV2) the query expects each filter as a
  // `{ value, operator }` object rather than a flat scalar. Pagination/sort
  // keys are passed through untouched.
  const queryInput = filterInputVariables
    ? convertToFilter({
        values: queryVariables,
        skipKeys: ["limit", "offset", "order", "sort1", "sort2", "sort3"],
      })
    : queryVariables;

  // Ensure query only runs when formik has initialized (has limit/offset in values)
  // This prevents premature query execution when skip changes from true to false
  const isFormikInitialized = formik?.values && 
    (formik.values.limit !== undefined || formik.values.offset !== undefined);
  
  const shouldSkip = skip?.(queryVariables) || !isFormikInitialized;

  const { previousData, data, loading, refetch } = useQuery(debouncedObj?.FETCH_QUERY, {
    skip: shouldSkip,
    variables: { input: { ...queryInput } },
    fetchPolicy,
    pollInterval,
  });

  let updateMutation = null;
  const refetchQueries = Array.isArray(REFETCH_QUERIES)
    ? REFETCH_QUERIES
    : [REFETCH_QUERIES];
  if (UPDATE_MUTATION) {
    // eslint-disable-next-line
    const [mutation] = useMutation(UPDATE_MUTATION, { refetchQueries });
    updateMutation = mutation;
  }

  const obj = data?.[dataKey] || previousData?.[dataKey] || {};
  const { count, edges = [] } = obj || {};

  useEffect(() => {
    if (!onQueryStateChange || shouldSkip || loading) return;

    onQueryStateChange({
      count,
      edges,
      queryVariables,
    });
  }, [count, edges, loading, onQueryStateChange, queryVariables, shouldSkip]);

  const _edges = edges?.map(x => ({
    ...x,
    rawFields: x,
    ...(pollInterval > 0
      ? {
          isnewData:
            new Date(x?.updatedAt) > new Date(new Date() - 14 * pollInterval),
        }
      : { isnewData: false }),
  }));

  const difference = obj?.edges?.filter(
    x => new Date(x?.updatedAt) > new Date(new Date() - 14 * pollInterval),
  );

  const handleRefresh = async () => {
    if (shouldSkip || typeof refetch !== "function") {
      return;
    }

    setIsRefreshing(true);
    try {
      await refetch({ input: { ...queryInput } });
    } finally {
      setIsRefreshing(false);
    }
  };

  const tableProps = {
    formik,
    headers,
    emptyMessage,
    isRefreshing,
    limit: formik?.values?.limit || limit,
    loading,
    onRefresh: handleRefresh,
    refreshDisabled: loading || isRefreshing || shouldSkip,
    rows: _edges,
    sequentialRowUpdates,
    updateMutation,
    showRefresh: canShowRefresh,
    variant,
    rowClassName,
    showNewDataColumn: pollInterval > 0,
    allowHorizontalScroll,
    ...(allowColumnWrap !== undefined && { allowColumnWrap }),
    onMutationError,
  };

  const onChange = values => {
    formik?.setValues({ ...formik?.values, ...values });
  };

  const paginationProps = {
    count,
    limit: formik?.values?.limit || limit,
    offset: formik?.values?.offset,
    onChange,
    ...pagination,
  };

  const containerPaddingClass = isSubTable ? "" : "px-4 sm:px-6 lg:px-8";
  const contentPaddingClass = isSubTable ? "" : "p-4 sm:px-0";

  // const filterBarProps = {
  //   inputs,
  //   formik,
  // };

  return (
    <div className={`min-w-0 w-full flex-grow ${containerPaddingClass} ${className}`}>
      <header>
        <div className="flex flex-grow flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {tabProps && <Tabs {...tabProps} />}
          <div className="flex flex-grow items-center justify-end">
            {/* showSearchFilter && <FilterBar {...filterBarProps} /> */}
          </div>
        </div>
      </header>
      {difference?.length > 0 && pollInterval > 0 && (
        <div className="flex flex-grow items-center">
          <div className="flex flex-grow items-center">
            <span className="text-sm text-black">
              {`${difference?.length} new data have arrived`}
            </span>
          </div>
        </div>
      )}
      <main className="min-w-0">
        <div className={`relative flex-col ${contentPaddingClass}`}>
          <TableUI {...tableProps} />
          {!loading && footerComponent && footerComponent(data?.[dataKey])}
          <div className="pt-6">
            {!loading && <Pagination {...paginationProps} />}
          </div>
        </div>
      </main>
    </div>
  );
};

export { Table };
